<template>
  <div class="TaskPanel">
    <b-overlay :show="saving" rounded="sm">
    <section class="task-panel-section">
      <div class="details-header">
        <h4 class="details-title">Task Details</h4>
        <div class="details-header-actions">
          <button type="button" class="btn btn-success btn-sm mr-2" @click="save" :disabled="saving">
            <b-spinner v-if="saving" small class="mr-2"></b-spinner>
            <i v-else class="fa fa-save mr-2"></i>
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="closePanel" :disabled="saving">Cancel</button>
        </div>
      </div>

      <div class="card panel task-details-main">
        <div class="task-details-main-item">
          <label class="detail-label-block">Task Name <span class="required">*</span></label>
          <input
            type="text"
            class="task-name-input"
            v-model="form.name"
            placeholder="Enter task name"
            ref="nameInput"
          />
        </div>

        <div class="task-details-main-item">
          <label class="detail-label-block">Description</label>
          <at at="@" :members="activeEmployees" name-key="Name" v-if="employees" ref="empat">
            <template slot="item" slot-scope="s">
              <span tabindex="-1">{{ s.item.Name }}</span>
            </template>
            <template slot="embeddedItem" slot-scope="s">
              <span>
                <span tabindex="-1" :data-teamio-employeeid="s.current.EmployeeID" class="badge mr-xs badge-primary badge-pill mt-1">{{ s.current.Name + " " }}</span>
              </span>
            </template>
            <div class="details-editor"
              ref="detailsEditor"
              placeholder="Add a description... Use @ to mention a teammate."
              contenteditable
              @input="onDetailsInput"></div>
          </at>
        </div>

        <div class="task-details-main-item">
          <label class="detail-label-block">Type</label>
          <input
            type="text"
            class="task-input"
            v-model="form.type"
            placeholder="e.g., Feature, Bug, Task"
          />
        </div>

        <div class="task-details-main-item">
          <label class="detail-label-block">Tags</label>
          <multiselect
            v-model="form.tags"
            :options="[]"
            track-by="name"
            :multiple="true"
            :taggable="true"
            tag-placeholder="Press Enter to add as a new tag"
            placeholder="Type to add tags..."
            @tag="onAddTag"
            label="name"
            :close-on-select="true"
            :clear-on-select="false"
            :preserve-search="false"
            :preselect-first="false"
            :hide-selected="true"
          />
        </div>

        <div class="readonly-rows">
          <div class="readonly-row">
            <span class="readonly-label"><i class="fa fa-columns mr-1"></i>Status</span>
            <span class="status-badge">{{ columnName || 'N/A' }}</span>
          </div>
          <div class="readonly-row">
            <span class="readonly-label"><i class="fa fa-calendar mr-1"></i>Created</span>
            <span class="date-badge">{{ formattedCreatedDate }}</span>
          </div>
        </div>
      </div>
    </section>
    </b-overlay>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import At from '@/pages/TaskList/vueat/At';
import { getNewlyMentionedEmployeeIDs } from '@/utils/taskMentions';
import { buildTagAlertPayload } from '@/utils/kanbanTagAlerts';
import { isEmailEnabled } from '@/utils/siteCapability';

export default {
  name: 'EditTaskPanel',
  components: { Multiselect, At },
  props: {
    task: { type: Object, required: true },
    columnIdx: { type: Number, required: true },
    taskIdx: { type: Number, required: true },
    columnName: { type: String, default: '' },
    dateFormatStr: { type: String, default: 'MM/DD/YYYY' },
    // Pass only the boardId. The panel reads the full board from the store
    // via a computed so we never pass a big reactive Vuex object as a prop
    // (which was creating an O(N^2) reactive graph across N TaskCards and
    // freezing the page on board switch).
    boardId: { type: String, required: true },
  },
  data() {
    return {
      form: {
        name: '',
        tags: [],
        type: '',
      },
      originalDetailsHTML: '',
      detailsHTML: '',
      saving: false,
    };
  },
  computed: {
    ...mapState('customer', {
      userID: state => state.userID,
      siteID: state => state.siteID,
      user: state => state.user,
      employees: state => state.employees,
      settings: state => state.settings,
    }),
    ...mapState('kanban', {
      boards: state => state.boards,
    }),
    // Resolve the board by ID from the store. Not reactive-tracked over
    // every TaskCard (unlike the old `board` prop), so switching boards
    // doesn't cause an O(N) re-render cascade.
    board() {
      if (!this.boards || !this.boardId) return { id: this.boardId, columns: [] };
      return this.boards.find(b => b.id === this.boardId) || { id: this.boardId, columns: [] };
    },
    activeEmployees() {
      if (!this.employees) return [];
      try {
        const all = this.employees.filter(e => e.Active === 1 || e.Active === '1');
        // On a private board, restrict the @-mention picker to board
        // members (Firestore rules hide the board from non-members, so a
        // mention would never reach them anyway).
        const acc = this.board && this.board.accessibleBy;
        if (Array.isArray(acc) && acc.length > 0 && !(acc.length === 1 && acc[0] === '*')) {
          return all.filter(e => acc.includes(e.EmployeeID));
        }
        return all;
      } catch (_) {
        return [];
      }
    },
    formattedCreatedDate() {
      const date = this.task && this.task.date;
      if (!date) return 'No date';
      try {
        if (date.seconds) {
          const ms = date.seconds * 1000 + Math.floor((date.nanoseconds || 0) / 1000000);
          return this.$moment ? this.$moment(ms).format(this.dateFormatStr) : new Date(ms).toLocaleDateString();
        }
        return this.$moment ? this.$moment(date).format(this.dateFormatStr) : new Date(date).toLocaleDateString();
      } catch (_) {
        return 'Invalid date';
      }
    },
  },
  mounted() {
    this.form = {
      name: this.task.name || '',
      tags: this.task.tags ? [...this.task.tags] : [],
      type: this.task.type || '',
    };
    this.originalDetailsHTML = this.task.details || '';
    this.$nextTick(() => {
      if (this.$refs.detailsEditor) {
        // Load existing details (plain text OR HTML from a prior save — both work in contenteditable).
        this.$refs.detailsEditor.innerHTML = this.task.details || '';
      }
      if (this.$refs.nameInput) this.$refs.nameInput.focus();
    });
    // Clear this user's unread Kanban mentions for THIS task (the Firestore
    // query filters to type=KANBAN + kanbanTaskId + unreadList contains me,
    // so other mentioned teammates stay unread).
    if (this.task && this.task.id && this.userID) {
      this.markKanbanTaskNotificationsRead({ taskId: this.task.id, employeeID: this.userID });
    }
  },
  methods: {
    onDetailsInput() {
      if (this.$refs.detailsEditor) {
        this.detailsHTML = this.$refs.detailsEditor.innerHTML || '';
      }
    },

    onAddTag(newTag) {
      if (typeof newTag === 'string' && newTag.includes('@')) {
        this.$toast.warning('Mentions go in the Description field, not Tags. Use @ in "Description" so they get notified.');
        return;
      }
      this.form.tags.push({ name: newTag });
    },

    ...mapActions('kanban', ['updateBoard', 'writeKanbanNotification', 'sendTagAlert']),
    ...mapActions('notifications', ['markKanbanTaskNotificationsRead']),

    // Fire a per-board tag alert if the board has tag-alerts enabled and the
    // edit ADDED a matching tag. Diffs the pre-edit snapshot (this.task.tags)
    // against the saved tags, so re-saving a card that already carried the tag
    // never re-fires. Fire-and-forget: sendTagAlert swallows all errors, so
    // this NEVER blocks the save. Called only AFTER the Firestore write
    // succeeds.
    maybeFireTagAlert(oldTags, newTags, cardName) {
      try {
        // EMAIL-CAPABILITY GATE: don't POST for a site that can't send email
        // (backend enforces the same; this is the cheap client-side guard).
        // Uses the canonical helper (src/utils/siteCapability.js).
        if (!isEmailEnabled(this.settings)) return;
        // The board-config + newly-added-tag decision + payload shape is shared
        // with AddTaskPanel — see buildTagAlertPayload (kanbanTagAlerts.js).
        const payload = buildTagAlertPayload({
          board: this.board,
          siteID: this.siteID,
          oldTags,
          newTags,
          cardName,
          addedByEmployeeID: this.userID,
          addedByName: this.user && this.user.Name,
        });
        if (payload) this.sendTagAlert(payload);
      } catch (_) { /* never block the save on alert dispatch */ }
    },

    async save() {
      if (!this.form.name) {
        this.$toast.error('Missing Task Name. Please try again.');
        return;
      }
      this.saving = true;
      try {
        const detailsHTML = this.$refs.detailsEditor
          ? (this.$refs.detailsEditor.innerHTML || '')
          : (this.detailsHTML || '');

        const newlyMentioned = getNewlyMentionedEmployeeIDs(
          this.originalDetailsHTML,
          detailsHTML
        ).filter(id => id !== this.userID);

        const updatedTask = {
          ...this.task,
          name: this.form.name,
          details: detailsHTML,
          tags: this.form.tags,
          type: this.form.type,
        };

        // Own the save INSIDE the panel. Mirrors TodoDetails.vue — await writes
        // first, THEN emit closePanel so the plugin's close transition doesn't
        // race with Firestore writes that re-render the parent.
        const updatedColumns = [...(this.board.columns || [])];
        if (updatedColumns[this.columnIdx] && updatedColumns[this.columnIdx].tasks) {
          updatedColumns[this.columnIdx].tasks[this.taskIdx] = updatedTask;
        }
        await this.updateBoard({
          boardId: this.board.id,
          updates: {
            columns: updatedColumns,
            lastModified: new Date(),
            lastModifiedBy: this.userID,
          },
        });

        // Diff the pre-edit tags snapshot vs the saved tags. Fire AFTER the
        // write resolves so an alert never lies about a card that failed to save.
        this.maybeFireTagAlert(this.task.tags || [], this.form.tags, this.form.name);

        // Write a notification for each newly-mentioned employee. Failures are
        // logged but don't block the save — the task update already succeeded.
        const creatorName = (this.user && this.user.Name) ? this.user.Name : 'a teammate';
        for (const assignee of newlyMentioned) {
          try {
            await this.writeKanbanNotification({
              audience: 'EMPLOYEE',
              employeeID: assignee,
              type: 'KANBAN',
              text: `You have been mentioned on a Kanban card by ${creatorName}`,
              siteID: this.siteID,
              createdAt: new Date(),
              unreadList: [assignee],
              boardId: this.board.id,
              kanbanTaskId: this.task.id,
            });
          } catch (_) { /* already logged by the store */ }
        }

        this.$toast.success('Task updated!');
        this.$emit('closePanel', { action: 'saved', updatedTask });
      } catch (e) {
        console.error('[EditTaskPanel] save failed:', e);
        this.$toast.error('Error saving task. Please try again.');
        // Keep the panel open for retry or manual cancel.
      } finally {
        this.saving = false;
      }
    },

    closePanel() {
      this.$emit('closePanel', { action: 'cancel' });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Styles mirror TodoDetails.vue. */

.TaskPanel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.task-panel-section {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
}

.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.details-header-actions {
  display: flex;
  align-items: center;
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding: 1rem;
}

.task-details-main-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label-block {
  display: block;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 0.375rem;
}

.required {
  color: #ef4444;
  font-weight: 700;
}

.task-name-input,
.task-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #fff;
  color: #0f172a;
  font-size: 0.9375rem;
  line-height: 1.5;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: #94a3b8;
  }
}

.details-editor {
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  background-color: white;
  color: #0f172a;
  transition: border-color 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &:empty::before {
    content: attr(placeholder);
    color: #94a3b8;
    pointer-events: none;
  }
}

.readonly-rows {
  margin-top: 1rem;
  border-top: 1px solid #f1f5f9;
  padding-top: 0.75rem;
}

.readonly-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.375rem 0;
  font-size: 0.875rem;
  color: #475569;
}

.readonly-label {
  min-width: 100px;
  font-weight: 600;
  color: #64748b;
}

.status-badge,
.date-badge {
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: 600;
}

.date-badge {
  background: #f1f5f9;
  color: #475569;
}

</style>
